import { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { addSyncListener } from '../../lib/syncService';
import { useOnlineStatus } from '../../lib/useOnlineStatus';
import { useAuth } from '../../contexts/AuthContext';
import { saveShelfSnapshot, queuePull } from '../../lib/offlineRecallPulls';
import { offlineShelf, hasNewRecall } from '../../lib/tillRecallData';
import { playRecallChime } from '../../lib/recallChime';
import { TillRecallRow } from './TillRecallRow';

const REFRESH_MS = 5 * 60 * 1000;

/** Lists recalled medicines still on this branch's shelf as a checklist staff tick off as they pull them. */
export const TillRecallBanner = ({ offlineReady, refreshKey }) => {
  const online = useOnlineStatus();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total_units: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const show = useCallback((next) => {
    setData(next);
    if (next.items.length && hasNewRecall(next.items)) {
      playRecallChime();
      setOpen(true);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      if (online) {
        const res = await api.get('/recall-returns/on-shelf');
        saveShelfSnapshot(res.data);
        show(res.data);
        return;
      }
    } catch (e) {
      if (e?.response) return;
    }
    if (offlineReady) show(await offlineShelf());
  }, [online, offlineReady, show]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    const unsub = addSyncListener((event) => { if (event === 'products_sync_complete') load(); });
    return () => { clearInterval(t); unsub(); };
  }, [load]);

  const pull = async (item, batch) => {
    setBusy(true);
    try {
      if (navigator.onLine) await api.post('/recall-returns/pulls', { product_id: item.product_id, batch_number: batch.batch_number });
      else await queuePull(item.product_id, batch.batch_number, user?.name || user?.email);
      toast.success(`${item.name} marked as pulled`, { description: 'It stays listed until the return to the supplier is logged.' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as pulled');
    } finally { setBusy(false); }
  };

  const undo = async (pullId) => {
    try { await api.delete(`/recall-returns/pulls/${pullId}`); await load(); } catch { toast.error('Could not undo'); }
  };

  if (!data.items.length) return null;
  const rows = data.items.flatMap((i) => i.batches.map((b) => ({ item: i, batch: b })));
  const left = rows.filter((r) => !r.batch.pulled).length;
  return (
    <div role="alert" className="mb-3 rounded-lg border border-red-300 bg-red-50 text-red-900" data-testid="till-recall-banner">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm" data-testid="till-recall-banner-toggle">
        <ShieldAlert className="h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <span className="flex-1">
          <strong data-testid="till-recall-banner-count">{data.items.length} recalled medicine{data.items.length === 1 ? '' : 's'} still on the shelf</strong>
          {' '}({data.total_units} unit{data.total_units === 1 ? '' : 's'}).{' '}
          <span data-testid="till-recall-banner-left">{left ? `${left} to pull — they cannot be sold.` : 'All pulled, awaiting return to supplier.'}</span>
          {!online && <span className="ml-1 text-xs text-red-700" data-testid="till-recall-banner-offline">From the copy saved on this terminal.</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <ul className="divide-y divide-red-200 border-t border-red-200 text-sm max-h-28 overflow-y-auto" data-testid="till-recall-banner-list">
          {rows.map(({ item, batch }) => (
            <TillRecallRow key={`${item.product_id}-${batch.batch_number}`} item={item} batch={batch}
              online={online} busy={busy} onPull={pull} onUndo={undo} />
          ))}
        </ul>
      )}
    </div>
  );
};
